import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import type { LedgerAccount, LedgerEntryInput, LedgerJournal } from "@/types/ledger";
import { useManagerEvents } from "@/hooks/useManagerEvents";

const today = () => new Date().toISOString().slice(0, 10);

const toNumber = (value: string): number => {
  if (!value) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

type LineState = {
  account_id: number | "";
  debit: string;
  credit: string;
  memo: string;
  event_id: string | "";
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: LedgerEntryInput) => Promise<void>;
  accounts: LedgerAccount[];
  journals: LedgerJournal[];
};

const EMPTY_LINE: LineState = {
  account_id: "",
  debit: "",
  credit: "",
  memo: "",
  event_id: "",
};

export function LedgerEntryForm({ open, onClose, onSubmit, accounts, journals }: Props) {
  const [date, setDate] = useState(() => today());
  const [journalId, setJournalId] = useState<number | "">("");
  const [memo, setMemo] = useState("");
  const [source, setSource] = useState("");
  const [lines, setLines] = useState<LineState[]>(() => [{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { events: managerEvents } = useManagerEvents(200);

  useEffect(() => {
    if (!open) return;
    setDate(today());
    setJournalId(journals[0]?.id ?? "");
    setMemo("");
    setSource("");
    setLines([
      { account_id: accounts[0]?.id ?? "", debit: "", credit: "", memo: "" },
      { account_id: accounts[1]?.id ?? accounts[0]?.id ?? "", debit: "", credit: "", memo: "" },
    ]);
    setError(null);
    setSubmitting(false);
  }, [open, journals, accounts]);

  const totals = useMemo(() => {
    return lines.reduce(
      (acc, line) => {
        return {
          debit: acc.debit + toNumber(line.debit),
          credit: acc.credit + toNumber(line.credit),
        };
      },
      { debit: 0, credit: 0 },
    );
  }, [lines]);

  const balanced = totals.debit === totals.credit && totals.debit > 0;
  const ready = accounts.length > 0 && journals.length > 0;

  const updateLine = (index: number, patch: Partial<LineState>) => {
    setLines((prev) => prev.map((line, idx) => (idx === index ? { ...line, ...patch } : line)));
  };

  const handleAddLine = () => {
    setLines((prev) => [...prev, { ...EMPTY_LINE, account_id: accounts[0]?.id ?? "" }]);
  };

  const handleRemoveLine = (index: number) => {
    setLines((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== index)));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) {
      setError("Add at least one account and journal before creating entries");
      return;
    }
    const parsedJournalId = typeof journalId === "number" ? journalId : Number(journalId);
    if (!parsedJournalId) {
      setError("Select a journal before posting");
      return;
    }
    if (!balanced) {
      setError("Debits and credits must balance before posting");
      return;
    }
    const hasInvalidLine = lines.some((line) => !line.account_id || (!line.debit && !line.credit));
    if (hasInvalidLine) {
      setError("Every line must include an account and either a debit or credit");
      return;
    }

    const payload: LedgerEntryInput = {
      date,
      journal_id: parsedJournalId,
      memo: memo.trim() || undefined,
      source: source.trim() || undefined,
      lines: lines.map((line) => ({
        account_id: typeof line.account_id === "number" ? line.account_id : Number(line.account_id),
        debit: line.debit ? Number(line.debit) : 0,
        credit: line.credit ? Number(line.credit) : 0,
        memo: line.memo?.trim() || undefined,
        event_id: line.event_id || undefined,
      })),
    };

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create entry");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-4xl rounded-2xl border border-border bg-background shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-100">New journal entry</h3>
            <p className="text-xs text-muted-foreground">Post balanced lines straight into the general ledger.</p>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          {!ready && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-primary-foreground dark:text-amber-100">
              Add at least one account and journal before creating entries.
            </div>
          )}
          {error && (
            <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-xs text-rose-100">
              {error}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-3">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              <span className="mb-2 block text-[11px]">Entry date</span>
              <input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                className="w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              <span className="mb-2 block text-[11px]">Journal</span>
              <select
                value={journalId}
                onChange={(event) => setJournalId(Number(event.target.value))}
                className="w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              >
                {journals.map((journal) => (
                  <option key={journal.id} value={journal.id}>
                    {journal.code} · {journal.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              <span className="mb-2 block text-[11px]">Source</span>
              <input
                type="text"
                value={source}
                onChange={(event) => setSource(event.target.value)}
                placeholder="Optional"
                className="w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              />
            </label>
          </div>
          <label className="block text-xs uppercase tracking-wide text-muted-foreground">
            <span className="mb-2 block text-[11px]">Memo</span>
            <textarea
              value={memo}
              onChange={(event) => setMemo(event.target.value)}
              className="h-20 w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
              placeholder="Optional overview"
            />
          </label>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Entry lines</span>
              <button
                type="button"
                className="text-emerald-300 hover:text-emerald-100"
                onClick={handleAddLine}
              >
                + Add line
              </button>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-border">
              <table className="min-w-full text-sm">
                <thead className="bg-background/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Account</th>
                    <th className="px-3 py-2 text-left font-medium">Memo & Event</th>
                    <th className="px-3 py-2 text-right font-medium">Debit</th>
                    <th className="px-3 py-2 text-right font-medium">Credit</th>
                    <th className="px-3 py-2 text-right font-medium"> </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={index} className="border-t border-border/80">
                      <td className="px-3 py-2">
                        <select
                          value={line.account_id}
                          onChange={(event) =>
                            updateLine(index, { account_id: Number(event.target.value) })
                          }
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground"
                        >
                          <option value="" disabled>
                            Select account
                          </option>
                          {accounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.code} · {account.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 space-y-2">
                        <select
                          value={line.event_id}
                          onChange={(event) => updateLine(index, { event_id: event.target.value })}
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground"
                        >
                          <option value="">No event tag</option>
                          {managerEvents.map((event) => (
                            <option key={event.id} value={event.id}>
                              {formatEventLabel(event)}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={line.memo}
                          onChange={(event) => updateLine(index, { memo: event.target.value })}
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground"
                          placeholder="Optional memo"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.debit}
                          onChange={(event) => updateLine(index, { debit: event.target.value, credit: "" })}
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-right text-xs text-foreground"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={line.credit}
                          onChange={(event) => updateLine(index, { credit: event.target.value, debit: "" })}
                          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-right text-xs text-foreground"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => handleRemoveLine(index)}
                          className="text-xs text-muted-foreground hover:text-rose-300"
                          disabled={lines.length <= 2}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-background/60 text-sm text-foreground/80">
                  <tr>
                    <td className="px-3 py-2 text-right font-semibold" colSpan={2}>
                      Totals
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-300">
                      {totals.debit.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-primary-foreground dark:text-amber-200">
                      {totals.credit.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {balanced ? (
                        <span className="text-emerald-300">Balanced</span>
                      ) : (
                        <span className="text-rose-300">Out of balance</span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!balanced || submitting || !ready}>
              {submitting ? "Posting…" : "Post entry"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

type ManagerEvent = {
  id: string
  name?: string
  event_date?: string
  city?: string
  state?: string
}

function formatEventLabel(event: ManagerEvent): string {
  const date = event.event_date ? new Date(event.event_date).toLocaleDateString() : "";
  const location = [event.city, event.state].filter(Boolean).join(", ");
  if (event.name) {
    return `${date ? `${date} · ` : ""}${event.name}${location ? ` (${location})` : ""}`;
  }
  return `${date} ${location}`.trim() || event.id;
}
